import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import Disclaimer from "@/components/Disclaimer";
import LossCategoryForm from "@/components/LossCategoryForm";
import { Button } from "@/components/ui/button";
import { getLossGuide, LOSS_CATEGORY_KEYS } from "@/data/lossCategoryGuides";
import {
  ExternalLink, ChevronRight, Shield, FileSearch, CheckCircle2,
} from "lucide-react";

const lossImageMap = {
  trading: "/assets/loss-categories/loss-trading.jpg",
  banking: "/assets/loss-categories/loss-banking.jpg",
  cyber: "/assets/loss-categories/loss-cyber.jpg",
  insurance: "/assets/loss-categories/loss-insurance.jpg",
  airline: "/assets/loss-categories/loss-airline.jpg",
  consumer: "/assets/loss-categories/loss-consumer.jpg",
  housing: "/assets/loss-categories/loss-housing.jpg",
  pension: "/assets/loss-categories/loss-pension.jpg",
};

const LossCategory = () => {
  const { categoryKey } = useParams();
  const { t, lang } = useT();
  const lg = t.lossGuide || {};
  const [showForm, setShowForm] = useState(false);

  if (!LOSS_CATEGORY_KEYS.includes(categoryKey)) {
    return <Navigate to="/" replace />;
  }

  const guide = getLossGuide(categoryKey);
  const lcItem = (t.lossCategories?.items || []).find((i) => i.key === categoryKey);
  const title = lcItem?.title || guide.title;
  const imageSrc = lossImageMap[categoryKey];

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-8 py-16 md:py-24" data-testid={`loss-category-${categoryKey}`}>
      {imageSrc && (
        <div className="relative h-48 md:h-56 rounded-xl overflow-hidden mb-8 border border-white/10">
          <img src={imageSrc} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-black/50 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono-alt mb-1">
              {guide.authority}
            </div>
            <h1 className={`font-display text-2xl md:text-3xl font-black text-white ${lang === "hi" ? "lang-hi" : ""}`}>
              {title}
            </h1>
          </div>
        </div>
      )}

      {!imageSrc && (
        <SectionHeading eyebrow={guide.authority} title={title} subtitle={guide.intro} />
      )}

      {imageSrc && (
        <p className={`text-white/65 leading-relaxed mb-8 ${lang === "hi" ? "lang-hi" : ""}`}>{guide.intro}</p>
      )}

      {/* Official portal guidelines */}
      <section className="surface-card p-6 md:p-8" data-testid="official-guidelines">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="h-5 w-5 text-[#D4AF37]" />
          <h2 className="font-display text-xl font-bold">{lg.officialTitle || "How to report on the official portal"}</h2>
        </div>

        <ol className="space-y-4">
          {guide.steps.map((step, i) => (
            <li key={i} className="flex gap-4">
              <span className="flex-shrink-0 h-7 w-7 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] ring-1 ring-[#D4AF37]/30 grid place-items-center text-xs font-bold font-mono-alt">
                {i + 1}
              </span>
              <p className={`text-sm text-white/75 leading-relaxed pt-0.5 ${lang === "hi" ? "lang-hi" : ""}`}>{step}</p>
            </li>
          ))}
        </ol>

        <div className="mt-8 flex flex-wrap gap-3">
          <a href={guide.portalUrl} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold gap-2" data-testid="official-portal-link">
              <ExternalLink className="h-4 w-4" />
              {lg.openPortal || "Open"} {guide.portalName}
            </Button>
          </a>
          {guide.secondaryPortalUrl && (
            <a href={guide.secondaryPortalUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white/25 hover:bg-white/5 text-white font-semibold gap-2">
                <ExternalLink className="h-4 w-4" />
                {guide.secondaryPortalName}
              </Button>
            </a>
          )}
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#FF3B30] hover:bg-[#cc2e25] text-white font-bold gap-2"
              data-testid="show-chintamat-form-btn"
            >
              {lg.teamHelpBtn || "Get CHINTAMAT team help"}
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </section>

      {/* Evidence checklist */}
      <section className="mt-6 surface-card p-6 md:p-8" data-testid="evidence-checklist">
        <div className="flex items-center gap-2 mb-4">
          <FileSearch className="h-5 w-5 text-[#D4AF37]" />
          <h2 className="font-display text-lg font-bold">{lg.evidenceTitle || "Evidence to gather before filing"}</h2>
        </div>
        <ul className="grid sm:grid-cols-2 gap-2">
          {guide.evidence.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-white/70">
              <CheckCircle2 className="h-4 w-4 text-[#D4AF37]/70 mt-0.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-6">
        <Disclaimer
          text={lg.disclaimer || "CHINTAMAT is not a government body or law firm. We guide you to official channels and help document your case."}
          variant="alert"
        />
      </div>

      {showForm && (
        <section className="mt-6 surface-card p-6 md:p-8" data-testid="chintamat-help-section">
          <LossCategoryForm categoryKey={categoryKey} guide={guide} />
        </section>
      )}

      <div className="mt-8 flex flex-wrap gap-4 text-sm">
        <Link to="/" className="text-[#D4AF37] hover:underline">{t.common.backHome || "← Back to home"}</Link>
        <Link to="/recovery" className="text-white/50 hover:text-white">{t.nav.recovery}</Link>
      </div>
    </div>
  );
};

export default LossCategory;
